import { useState } from "react";
import { Card, CardContent, CardMedia, Box, useTheme } from "@mui/material";
import { TaskerMessage, TaskerConfig } from "@t3/shared";
import { useFirebaseStorageUrl } from "@/context/FirebaseStorageUrlContext";
import { getStatusColor } from "./utils/statusHelpers";
import { ImageDialog } from "./ImageDialog";
import { ContactInfoSection } from "./ContactInfoSection";
import { StatusOnlySection } from "./StatusOnlySection";
import { MessageBodySection } from "./MessageBodySection";
import { RejectedReasonsSection } from "./RejectedReasonsSection";

interface TaskerMessageCardProps {
  message: TaskerMessage;
  size?: string;
  showContactInfo?: boolean;
  userTaskerConfigs?: TaskerConfig[];
}

export const TaskerMessageCard = ({
  message,
  size = "420px",
  showContactInfo = true,
  userTaskerConfigs = [],
}: TaskerMessageCardProps) => {
  const theme = useTheme();
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const { url: mediaFileUrl } = useFirebaseStorageUrl(
    message.mediaFiles?.[0]?.url,
  );

  const handleImageClick = () => {
    setIsImageDialogOpen(true);
  };

  const handleImageDialogClose = () => {
    setIsImageDialogOpen(false);
  };

  return (
    <>
      <Card
        data-cypress="message-card"
        sx={{
          width: "100%",
          minWidth: 0,
          maxWidth: size === "100%" ? "100%" : size,
          height: size === "100%" ? "100%" : "auto",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
          borderRadius: 0.5,
          border: "1px solid #888",
          paddingBottom: 2,
        }}
      >
        <CardMedia
          component="img"
          sx={{
            width: "100%",
            height: size === "100%" ? "auto" : size,
            aspectRatio: size === "100%" ? "1" : "auto",
            objectFit: "cover",
            cursor: "pointer",
            flex: "0 0 auto",
          }}
          image={mediaFileUrl || ""}
          alt="Post image"
          onClick={handleImageClick}
        />

        {/* Status indicator bar */}
        <Box
          sx={{
            height: "4px",
            width: "100%",
            flex: "0 0 auto",
            bgcolor:
              theme.palette[
                getStatusColor(message.reviewStatus) as
                  | "success"
                  | "error"
                  | "warning"
              ].main,
          }}
        />

        <CardContent
          sx={{
            p: 0,
            flex: "1 1 auto",
            display: "flex",
            flexDirection: "column",
            pb: 0,
            "&:last-child": { pb: 0 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 0 }}>
            <Box sx={{ flex: 1 }}>
              {showContactInfo ? (
                <ContactInfoSection
                  firstName={message.messageMeta.firstName}
                  lastName={message.messageMeta.lastName}
                  phoneNumber={message.phoneNumber}
                  city={message.messageMeta.city}
                  state={message.messageMeta.state}
                  reviewStatus={message.reviewStatus}
                  timestamp={message.timestamp}
                />
              ) : (
                <StatusOnlySection
                  reviewStatus={message.reviewStatus}
                  rewardOptions={message.rewardOptions}
                  userTaskerConfigs={userTaskerConfigs}
                  eligibleProgramIds={message.eligibleProgramIds}
                />
              )}
            </Box>
          </Box>

          <MessageBodySection
            body={message.message.body}
            isApproved={message.reviewStatus === "approved"}
          />

          <RejectedReasonsSection
            message={message}
            userTaskerConfigs={userTaskerConfigs}
          />
        </CardContent>
      </Card>

      <ImageDialog
        open={isImageDialogOpen}
        imageUrl={mediaFileUrl || ""}
        onClose={handleImageDialogClose}
        altText="Post image"
      />
    </>
  );
};
